/**
 * Tầng ĐO — Prompt.md §13 + BLOCKER #1 Option C.
 *
 * Nguyên tắc của Option C: **"đo khác nhau, phán xét giống nhau"**. File này giữ toàn bộ phần
 * "đo khác nhau" cho cả hai placement; calibration-core và posture-core đều gọi vào đây, nên
 * baseline và runtime chắc chắn nói cùng một thứ tiếng.
 *
 * Vì sao nằm ở `vision-core` chứ không ở `posture-core` như bản nháp của plan: baseline (§11)
 * là trung vị của chính các số đo này, nên calibration-core **phải** dùng lại chúng. Đặt tầng đo
 * trong posture-core sẽ tạo phụ thuộc vòng (calibration → posture → calibration) hoặc, tệ hơn,
 * hai bản công thức song song. `vision-core` là tầng thấp nhất mà cả hai đều đã phụ thuộc, và
 * việc này thuần hình học — chưa có một phán xét tư thế nào.
 */

import type { PostureConfig } from "@studystraight/shared-config";
import type {
  BodyLandmark,
  CameraPlacement,
  LandmarkMap,
  LandmarkName,
  MeasurementKey,
  Metric,
  PostureMeasurement,
  PostureMetricKey,
  VisionResult,
} from "@studystraight/shared-types";

import {
  distance,
  midpoint,
  perpendicularDistance,
  signedAngleFromHorizontal,
  signedAngleFromVertical,
} from "./geometry.js";

/** Thứ tự cố định để test và debug log đọc được nhất quán. */
export const MEASUREMENT_KEYS: readonly MeasurementKey[] = [
  "headAngle",
  "torsoAngle",
  "shoulderTilt",
  "faceScale",
  "shoulderWidth",
  "headPosition",
];

/**
 * Metric của §13 được dựng từ (những) chiều đo nào, theo từng placement.
 *
 * Bảng này là **nguồn sự thật duy nhất** cho câu hỏi "metric này có đo được không": calibration
 * dùng nó để điền `PostureBaseline.unavailableMetrics`, và extractor (Bước 9) dùng đúng nó để
 * quyết định `Metric.available`. Mảng rỗng = placement này không đo được, vĩnh viễn.
 *
 * `shoulderWidth` KHÔNG có trong danh sách của `distanceRatio` vì nó là tín hiệu **phụ** (DU #2):
 * thiếu nó thì `distanceRatio` vẫn tính được từ `faceScale`, chỉ kém nhạy hơn.
 */
export const METRIC_SOURCES: Record<
  CameraPlacement,
  Record<PostureMetricKey, readonly MeasurementKey[]>
> = {
  FRONTAL: {
    // Ở chính diện, góc tai↔vai gần như không đổi khi trẻ cúi đầu (DU #4) — khoảng hở dọc
    // mũi↔vai mới là tín hiệu head-down thực sự.
    headAngle: ["headPosition"],
    torsoAngle: ["torsoAngle"],
    shoulderTilt: ["shoulderTilt"],
    distanceRatio: ["faceScale"],
  },
  SIDE: {
    headAngle: ["headAngle"],
    torsoAngle: ["torsoAngle"],
    // Hai vai chồng nhau ở góc nhìn ngang: không đo được, không phải "đo ra 0 độ".
    shoulderTilt: [],
    distanceRatio: ["faceScale"],
  },
};

export interface PlacementMeasurer {
  readonly placement: CameraPlacement;
  measure(vision: VisionResult, config: PostureConfig): PostureMeasurement;
}

const UNAVAILABLE: Metric = { value: Number.NaN, available: false };

/**
 * `NaN`/`Infinity` → `available: false`. Đây là chốt cuối biến "không đo được" của hình học
 * thành tri-state, thay vì để 0 lọt vào công thức và bị hiểu là "tư thế hoàn hảo".
 */
function metric(value: number): Metric {
  return Number.isFinite(value) ? { value, available: true } : { ...UNAVAILABLE };
}

function visible(lm: BodyLandmark | undefined, minVisibility: number): BodyLandmark | null {
  if (lm === undefined) return null;
  return (lm.visibility ?? 1) >= minVisibility ? lm : null;
}

/** Cả hai điểm phải thấy được — dùng cho các đại lượng đối xứng (vai↔vai, tai↔tai). */
function bothVisible(
  landmarks: LandmarkMap,
  a: LandmarkName,
  b: LandmarkName,
  minVisibility: number,
): readonly [BodyLandmark, BodyLandmark] | null {
  const first = visible(landmarks[a], minVisibility);
  const second = visible(landmarks[b], minVisibility);
  return first && second ? [first, second] : null;
}

/**
 * Điểm rõ hơn trong một cặp trái/phải — dùng cho mode SIDE, nơi một bên cơ thể luôn bị chính
 * cơ thể che. Đòi hỏi thấy cả hai bên ở góc nhìn ngang là tự loại bỏ chính mode đó.
 */
function clearerOf(
  landmarks: LandmarkMap,
  a: LandmarkName,
  b: LandmarkName,
  minVisibility: number,
): BodyLandmark | null {
  const first = visible(landmarks[a], minVisibility);
  const second = visible(landmarks[b], minVisibility);
  if (first && second) return (first.visibility ?? 1) >= (second.visibility ?? 1) ? first : second;
  return first ?? second;
}

/** `NaN` khi không có điểm — mọi hàm hình học phía sau tự lan `NaN` ra tới `metric()`. */
const NO_POINT: BodyLandmark = { x: Number.NaN, y: Number.NaN };

function measureFrontal(vision: VisionResult, config: PostureConfig): PostureMeasurement {
  const { landmarks, frameAspectRatio: ar, timestamp, confidence } = vision;
  const minVis = config.minLandmarkVisibility;

  const shoulders = bothVisible(landmarks, "leftShoulder", "rightShoulder", minVis);
  const ears = bothVisible(landmarks, "leftEar", "rightEar", minVis);
  const hips = bothVisible(landmarks, "leftHip", "rightHip", minVis);
  const nose = visible(landmarks.nose, minVis);

  const shoulderMid = shoulders ? midpoint(shoulders[0], shoulders[1]) : null;
  const shoulderWidth = shoulders ? distance(shoulders[0], shoulders[1], ar) : Number.NaN;
  const earMid = ears ? midpoint(ears[0], ears[1]) : null;
  const hipMid = hips ? midpoint(hips[0], hips[1]) : null;

  return {
    timestamp,
    confidence,
    headAngle: metric(signedAngleFromVertical(earMid ?? NO_POINT, shoulderMid ?? NO_POINT, ar)),
    torsoAngle: metric(signedAngleFromVertical(shoulderMid ?? NO_POINT, hipMid ?? NO_POINT, ar)),
    shoulderTilt: metric(
      shoulders ? signedAngleFromHorizontal(shoulders[0], shoulders[1], ar) : Number.NaN,
    ),
    faceScale: metric(ears ? distance(ears[0], ears[1], ar) : Number.NaN),
    shoulderWidth: metric(shoulderWidth),
    // Chuẩn hoá theo chiều rộng vai, không dùng pixel thô: nếu không, trẻ dịch ra xa camera là
    // headPosition tụt xuống và hệ thống báo "cúi đầu" trong khi trẻ vừa ngồi thẳng hơn.
    headPosition: metric(
      nose && shoulderMid && shoulderWidth > 1e-6
        ? (shoulderMid.y - nose.y) / shoulderWidth
        : Number.NaN,
    ),
  };
}

function measureSide(vision: VisionResult, config: PostureConfig): PostureMeasurement {
  const { landmarks, frameAspectRatio: ar, timestamp, confidence } = vision;
  const minVis = config.minLandmarkVisibility;

  const ear = clearerOf(landmarks, "leftEar", "rightEar", minVis);
  const shoulder = clearerOf(landmarks, "leftShoulder", "rightShoulder", minVis);
  const hip = clearerOf(landmarks, "leftHip", "rightHip", minVis);
  const nose = visible(landmarks.nose, minVis);

  const torsoLength = shoulder && hip ? distance(shoulder, hip, ar) : Number.NaN;
  const hasTorso = torsoLength > 1e-6;

  return {
    timestamp,
    confidence,
    headAngle: metric(signedAngleFromVertical(ear ?? NO_POINT, shoulder ?? NO_POINT, ar)),
    torsoAngle: metric(signedAngleFromVertical(shoulder ?? NO_POINT, hip ?? NO_POINT, ar)),
    // Hai vai chồng nhau ở góc ngang → con số tính được là rác, nên không tính.
    shoulderTilt: { ...UNAVAILABLE },
    // Thay cho khoảng cách hai tai (ở profile view hai tai cũng chồng nhau): độ nhô của mũi ra
    // khỏi trục vai↔hông. Trẻ rướn người về phía màn hình thì đại lượng này tăng — cùng chiều
    // với faceScale của FRONTAL, nên `distanceRatio` giữ đúng một ý nghĩa ở cả hai mode.
    faceScale: metric(
      nose && shoulder && hip && hasTorso
        ? perpendicularDistance(nose, shoulder, hip, ar) / torsoLength
        : Number.NaN,
    ),
    shoulderWidth: { ...UNAVAILABLE },
    headPosition: metric(
      nose && shoulder && hasTorso ? (shoulder.y - nose.y) / torsoLength : Number.NaN,
    ),
  };
}

const MEASURERS: Record<CameraPlacement, PlacementMeasurer> = {
  FRONTAL: { placement: "FRONTAL", measure: measureFrontal },
  SIDE: { placement: "SIDE", measure: measureSide },
};

export function createMeasurer(placement: CameraPlacement): PlacementMeasurer {
  return MEASURERS[placement];
}

/** Tiện dụng cho caller đã có `AppConfig`; tương đương `createMeasurer(placement).measure(...)`. */
export function measurePosture(
  vision: VisionResult,
  placement: CameraPlacement,
  config: PostureConfig,
): PostureMeasurement {
  return MEASURERS[placement].measure(vision, config);
}
